import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupees, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, TrendingUp, Info, Plus } from "lucide-react";

const METHOD_META = {
  manual:           { label: "Manual snapshot",              desc: "Type the market value directly (from a valuer's certificate)." },
  cagr:             { label: "CAGR appreciation",            desc: "P × (1 + r)^t — for land / private equity with an expected annual growth rate." },
  loan_pv:          { label: "Loan PV",                      desc: "Principal outstanding + accrued simple interest — for loans given/taken." },
  book_value:       { label: "Book value × P/B",             desc: "For company shares — BV × price-to-book × ownership%." },
  pe_multiple:      { label: "Earnings × PE multiple",       desc: "For company shares — annual earnings × PE × ownership%." },
  last_round:       { label: "Last funding round",           desc: "Latest company valuation × ownership%." },
  ebitda_multiple:  { label: "EBITDA × multiple (Franchise)", desc: "(TTM EBITDA × multiple − net debt) × ownership%. Industry multiples for F&B: 2-5x." },
  nav:              { label: "NAV × units",                  desc: "For mutual funds / SIP / AIF — units × latest NAV per unit." },
};

const AUTO_METHOD = {
  land: "cagr",
  loan: "loan_pv",
  loan_taken: "loan_pv",
  company_share: "last_round",
  business_franchise: "ebitda_multiple",
  sebi_startup_fund: "nav",
  sip: "nav",
  lumpsum: "nav",
};

const INPUT_FIELDS = {
  cagr:            [{ k: "annual_rate_percent", l: "Annual growth rate (%)", type: "number", ph: "e.g. 12" }],
  loan_pv:         [
    { k: "principal_outstanding", l: "Principal outstanding (Rs)", type: "number" },
    { k: "annual_rate_percent",   l: "Interest rate (% p.a.)",     type: "number" },
    { k: "interest_from_date",    l: "Interest accruing from",     type: "date" },
  ],
  book_value:      [
    { k: "book_value",       l: "Company book value (Rs)", type: "number" },
    { k: "p2b_multiple",     l: "Price/Book multiple",     type: "number", ph: "1.5" },
    { k: "ownership_percent",l: "Your ownership (%)",      type: "number", ph: "10" },
  ],
  pe_multiple:     [
    { k: "annual_earnings",  l: "Annual net earnings (Rs)", type: "number" },
    { k: "pe_multiple",      l: "PE multiple",              type: "number", ph: "12" },
    { k: "ownership_percent",l: "Your ownership (%)",       type: "number" },
  ],
  last_round:      [
    { k: "company_valuation", l: "Company valuation (Rs)", type: "number" },
    { k: "ownership_percent", l: "Your ownership (%)",     type: "number" },
  ],
  ebitda_multiple: [
    { k: "ttm_ebitda",        l: "Trailing 12-month EBITDA (Rs)", type: "number" },
    { k: "ebitda_multiple",   l: "Industry multiple",             type: "number", ph: "3" },
    { k: "net_debt",          l: "Net debt (Rs)",                 type: "number", ph: "0" },
    { k: "ownership_percent", l: "Your ownership (%)",            type: "number", ph: "100" },
  ],
  nav:             [
    { k: "units",         l: "Units held",         type: "number" },
    { k: "nav_per_unit",  l: "NAV per unit (Rs)",  type: "number" },
  ],
};

/** Full valuation history + KPIs panel used inside InvestmentDetail. */
export function ValuationPanel({ investment, valuations, canEdit, onOpen }) {
  const [mv, setMv] = useState(null);
  const [xirr, setXirr] = useState(null);
  useEffect(() => {
    if (!investment) return;
    api.get(`/investments/${investment.id}/market-value`).then((r) => setMv(r.data));
    api.get(`/investments/${investment.id}/xirr`).then((r) => setXirr(r.data));
  }, [investment, valuations]);

  const cost = Number(investment?.amount_invested || 0);
  const value = Number(mv?.value || 0);
  const gain = value - cost;
  const gainPct = cost ? (gain / cost) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Cost" value={formatRupees(cost)} testId="mv-cost" />
        <KPI label="Market value" value={formatRupees(value)} tone="text-slate-900" testId="mv-value" />
        <KPI label="Unrealised gain"
          value={`${gain >= 0 ? "+" : ""}${formatRupees(gain)}`}
          tone={gain >= 0 ? "text-emerald-700" : "text-red-600"}
          sub={`${gainPct >= 0 ? "+" : ""}${gainPct.toFixed(2)}%`}
          testId="mv-gain" />
        <KPI label="XIRR (annualised)"
          value={xirr?.xirr_percent !== null && xirr?.xirr_percent !== undefined
            ? `${xirr.xirr_percent.toFixed(2)}%`
            : "—"}
          tone="text-slate-900" testId="mv-xirr" />
      </div>

      {/* Method banner */}
      {mv && (
        <div className="border border-slate-200 bg-slate-50 p-4 rounded-md text-[12px] flex items-start gap-2">
          <Info className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
          <div className="flex-1">
            <div className="text-slate-800">
              Valued using <span className="font-medium">{METHOD_META[mv.method]?.label || mv.method}</span>
              {" "}<span className="text-slate-500">· source: {mv.source}</span>
              {" "}<span className="text-slate-500">· as of {formatDate(mv.as_of_date)}</span>
            </div>
            {mv.breakdown?.formula && (
              <div className="text-[11px] text-slate-500 font-mono-fin mt-1">{mv.breakdown.formula}</div>
            )}
          </div>
          {canEdit && (
            <Button size="sm" onClick={onOpen} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="add-valuation-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New snapshot
            </Button>
          )}
        </div>
      )}

      {/* History */}
      <div className="border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">As of</th>
              <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Method</th>
              <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Value</th>
              <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Note</th>
            </tr>
          </thead>
          <tbody>
            {valuations.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-slate-500">
                <TrendingUp className="h-5 w-5 mx-auto text-slate-300 mb-2" />
                No manual valuation snapshots yet.
                {canEdit && " Click New snapshot to add one — pick a method and the app computes the value for you."}
              </td></tr>
            )}
            {valuations.map((v) => (
              <tr key={v.id} className="border-b border-slate-100" data-testid={`val-row-${v.id}`}>
                <td className="p-3">{formatDate(v.as_of_date || v.date || "")}</td>
                <td className="p-3 text-slate-700">
                  {METHOD_META[v.method || "manual"]?.label || v.method || "Manual"}
                </td>
                <td className="p-3 text-right font-mono-fin">{formatRupees(v.value)}</td>
                <td className="p-3 text-slate-500 text-[12px]">{v.notes || v.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Full-fledged valuation dialog with method-aware inputs and live preview. */
export function ValuationDialog({ open, onOpenChange, investment, form, setForm, onSave }) {
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const method = form.method || "manual";

  // Auto-default method the first time we open based on investment type
  useEffect(() => {
    if (open && form.method === "manual" && investment && AUTO_METHOD[investment.type]) {
      setForm((f) => ({ ...f, method: AUTO_METHOD[investment.type], inputs: {} }));
    }
    // eslint-disable-next-line
  }, [open, investment?.type]);

  useEffect(() => {
    if (!open || !investment) return;
    if (method === "manual") { setPreview(null); return; }
    const params = new URLSearchParams({
      method,
      as_of: form.date || "",
      inputs_json: JSON.stringify(form.inputs || {}),
    });
    let cancelled = false;
    api.get(`/investments/${investment.id}/market-value/preview?${params.toString()}`)
      .then((r) => { if (!cancelled) { setPreview(r.data); setPreviewErr(null); } })
      .catch((e) => { if (!cancelled) { setPreview(null); setPreviewErr(e?.response?.data?.detail || "preview failed"); } });
    return () => { cancelled = true; };
  }, [open, investment, method, form.inputs, form.date]);

  const fields = INPUT_FIELDS[method] || [];
  const updInput = (k, v) => setForm({ ...form, inputs: { ...(form.inputs || {}), [k]: v } });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="val-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New valuation snapshot</DialogTitle>
          <DialogDescription>Pick a valuation method — inputs and preview update live.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={(v) => setForm({ ...form, method: v, inputs: {} })}>
              <SelectTrigger className="mt-1.5" data-testid="val-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(METHOD_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">{METHOD_META[method]?.desc}</p>
          </div>

          <div>
            <Label>As of date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="val-date" />
          </div>

          {method === "manual" ? (
            <div>
              <Label>Value (Rs)</Label>
              <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="mt-1.5 font-mono-fin text-right" data-testid="val-value" />
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50 space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Formula inputs
              </div>
              <div className="grid grid-cols-2 gap-3">
                {fields.map((f) => (
                  <div key={f.k}>
                    <Label className="text-[11px]">{f.l}</Label>
                    <Input type={f.type} placeholder={f.ph || ""}
                      value={form.inputs?.[f.k] ?? ""}
                      onChange={(e) => updInput(f.k, e.target.value)}
                      className="mt-1 font-mono-fin text-right h-9"
                      data-testid={`val-input-${f.k}`} />
                  </div>
                ))}
              </div>
              {preview && (
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Live preview</div>
                  <div className="text-xl font-mono-fin font-semibold text-slate-900 mt-1" data-testid="val-preview">
                    {formatRupees(preview.value)}
                  </div>
                  {preview.breakdown?.formula && (
                    <div className="text-[10px] font-mono-fin text-slate-500 mt-1">{preview.breakdown.formula}</div>
                  )}
                </div>
              )}
              {previewErr && (
                <div className="text-[10px] text-red-600">{previewErr}</div>
              )}
            </div>
          )}

          <div>
            <Label>Note</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" data-testid="val-note" placeholder="Source, valuer, assumption context" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} className="bg-red-600 hover:bg-red-700 text-white" data-testid="val-save">Save snapshot</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, tone, sub, testId }) {
  return (
    <div className="p-4 border border-slate-200 bg-white rounded-md">
      <div className="text-[10px] tracking-wider uppercase text-slate-500">{label}</div>
      <div className={`mt-1.5 font-mono-fin text-xl font-semibold ${tone || "text-slate-900"}`} data-testid={testId}>{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 font-mono-fin ${tone || "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}
